import { useEffect, useState } from "react";
import SiteMenu from "@/components/SiteMenu";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import StarField from "@/components/StarField";
import CelestialBackground from "@/components/CelestialBackground";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("error");
          return;
        }
      } catch {
        /* keep trying */
      }
      if (attempts >= 12) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params, refresh]);

  const isGift = info?.reading_type === "gift";
  const isPack = (info?.plan || info?.lookup_key || "").startsWith("chat_pack");
  const isReading =
    info?.plan === "reading_single" ||
    (info?.lookup_key || "").startsWith("reading_single") ||
    !!info?.reading_type;

  return (
    <div className="app-shell relative text-starlight">
      <CelestialBackground />
      <SiteMenu floating />
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-8">
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin gold-text mb-4" size={36} />
            <p className="text-xl italic">Aligning the stars with your order…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="gold-text mb-4" size={48} />
            <h1 className="text-3xl font-light mb-2">{isGift ? "Your gift is on its way" : isPack ? "10 questions added" : isReading ? "Your reading awaits" : "Welcome to Premium"}</h1>
            <p className="text-[#A7B0C0] mb-6">
              {isGift
                ? "We'll email your friend a link to open it on the date you chose. You can check on it any time in Gifts."
                : isPack
                ? "Astra Interactive is ready for your next questions. They never expire."
                : isReading
                ? "Your personalized in-depth reading has been written and saved to your archive."
                : "Every Love, Money, Weekly and Monthly forecast is now unlocked."}
            </p>
            <button
              data-testid="btn-payment-continue"
              onClick={() => navigate(isGift ? "/gifts" : isPack ? "/ask" : isReading ? "/history" : "/today")}
              className="bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium px-8 py-3 rounded-full transition-colors"
            >
              {isGift ? "See my gifts" : isPack ? "Ask Astra Interactive" : isReading ? "Read it now" : "See my sky"}
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="text-red-300 mb-4" size={44} />
            <h1 className="text-2xl font-light mb-2">We couldn't confirm your payment</h1>
            <p className="text-[#A7B0C0] mb-6">If you were charged, it will appear shortly. Otherwise please try again.</p>
            <button onClick={() => navigate("/today")} className="border border-[#D4AF37]/40 px-8 py-3 rounded-full">
              Back to Astra
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="app-shell relative text-starlight">
      <CelestialBackground />
      <SiteMenu floating />
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen text-center px-8">
        <XCircle className="text-[#A7B0C0] mb-4" size={44} />
        <h1 className="text-2xl font-light mb-2">Checkout cancelled</h1>
        <p className="text-[#A7B0C0] mb-6">No charge was made. The stars will wait for you.</p>
        <button
          onClick={() => navigate("/today")}
          className="bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium px-8 py-3 rounded-full transition-colors"
        >
          Return to Astra
        </button>
      </div>
    </div>
  );
}
